import type { WorkSchedule } from '../types/employee';

/** schedule.date('YYYY-MM-DD' 또는 ISO)에서 날짜 부분만 추출 */
export const scheduleDateStr = (schedule: Pick<WorkSchedule, 'date'>): string => {
  const d = schedule.date;
  if (typeof d === 'string') return d.includes('T') ? d.split('T')[0] : d;
  return String(d);
};

/** 특정 날짜(YYYY-MM-DD)의 스케줄만 필터 */
export const getSchedulesOnDate = (
  schedules: WorkSchedule[],
  dateStr: string,
): WorkSchedule[] => schedules.filter((s) => scheduleDateStr(s) === dateStr);

/** 같은 직원+가계부+날짜 스케줄이 이미 있는지 */
export const hasExistingSchedule = (
  schedules: WorkSchedule[],
  employeeId: string,
  accountNumber: number,
  dateStr: string,
): boolean =>
  schedules.some(
    (s) =>
      s.employee_id === employeeId &&
      s.account_number === accountNumber &&
      scheduleDateStr(s) === dateStr,
  );

/** "YYYY-MM-DDTHH:mm:ss(.sss)" 의 날짜부분만 targetDateStr 로 교체 (시각 보존) */
export const reTimeIso = (iso: string, targetDateStr: string): string => {
  if (!iso) return iso;
  const tIdx = iso.indexOf('T');
  if (tIdx === -1) return iso;
  return `${targetDateStr}${iso.slice(tIdx)}`;
};

/** Date 의 연·월·일만 targetDateStr 로 교체, 시·분·초 보존 (로컬 기준) */
export const reTimeDate = (src: Date, targetDateStr: string): Date => {
  const [y, m, d] = targetDateStr.split('-').map(Number);
  return new Date(y, m - 1, d, src.getHours(), src.getMinutes(), src.getSeconds());
};

const pad = (n: number): string => String(n).padStart(2, '0');

/**
 * Date → Flutter 저장형식 로컬 벽시계 문자열 "YYYY-MM-DDTHH:MM:SS.000" (Z 없음).
 * toISOString(UTC 변환)을 쓰지 않아 시각이 타임존만큼 어긋나는 것을 방지한다.
 * (드래그 이동의 localIso 와 동일한 의미 — 화면에 보이는 시각을 그대로 저장)
 */
export const toWallClock = (d: Date): string =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T` +
  `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}.000`;

/**
 * 원본 스케줄을 targetDateStr(YYYY-MM-DD) 로 옮긴 새 scheduleData(id 제외) 생성.
 * 직원 중복필드는 원본 그대로, **시각은 화면에 보이는 그대로 보존**, 날짜만 교체한다.
 *
 * 시각 보존 핵심: 시간 필드를 미리 직렬화해서 넘긴다.
 * - start_time_1/end_time_1/start_time_2/end_time_2 → 로컬 벽시계 문자열(Z 없음).
 *   addWorkScheduleToShared 는 직접 전달된 start_time_1 등을 변환 없이 저장하므로
 *   원본과 같은 시각 digit 이 그대로 들어간다.
 * - start_time/end_time(호환 필드) → 벽시계 + 'Z'. addWorkScheduleToShared 가
 *   new Date(x).toISOString() 로 재변환하는데, 'Z' 를 붙여 넘기면 같은 digit 으로 복원된다.
 * - workTime1/workTime2 객체는 넘기지 않는다(넘기면 toISOString 로 UTC 변환되어 어긋남).
 */
export const buildCopiedScheduleData = (
  source: WorkSchedule,
  targetDateStr: string,
): Record<string, any> => {
  const data: Record<string, any> = {
    date: targetDateStr,
    employee_id: source.employee_id,
    employee_name: source.employee_name,
    account_number: source.account_number,
    break_time: source.break_time ?? '1시간',
    is_favorite: false,
    chat_messages: [],
    // 직원 정보 중복 필드 (Flutter 호환)
    mp: source.mp,
    isHourly: source.isHourly,
    hourlyRate: source.hourlyRate,
    monthlySalary: source.monthlySalary,
    weeklyOffDays: source.weeklyOffDays,
    weeklyWorkHours: source.weeklyWorkHours,
    taxRate: source.taxRate,
    dependents: source.dependents,
    hasFourInsurance: source.hasFourInsurance,
    payday: source.payday,
    isweeklyWork: source.isweeklyWork,
    hire_date: source.hire_date,
    severance_pay: source.severance_pay ?? 0,
  };

  if (source.templateId) data.templateId = source.templateId;

  // 1차/2차 근무 시각: 벽시계 문자열로 직접 직렬화 (UTC 변환 회피)
  let primaryStart: Date | null = null;
  let primaryEnd: Date | null = null;

  if (source.workTime1) {
    primaryStart = reTimeDate(source.workTime1.start as Date, targetDateStr);
    primaryEnd = reTimeDate(source.workTime1.end as Date, targetDateStr);
    data.start_time_1 = toWallClock(primaryStart);
    data.end_time_1 = toWallClock(primaryEnd);
  }
  if (source.workTime2) {
    data.start_time_2 = toWallClock(reTimeDate(source.workTime2.start as Date, targetDateStr));
    data.end_time_2 = toWallClock(reTimeDate(source.workTime2.end as Date, targetDateStr));
  }

  // 템플릿이 아닌 커스텀 스케줄: start_time/end_time 으로 1차 시각 구성
  if (!primaryStart && source.start_time && source.end_time) {
    primaryStart = reTimeDate(new Date(source.start_time), targetDateStr);
    primaryEnd = reTimeDate(new Date(source.end_time), targetDateStr);
    data.start_time_1 = toWallClock(primaryStart);
    data.end_time_1 = toWallClock(primaryEnd);
  }

  // 호환 필드 start_time/end_time: 'Z' 를 붙여 addWorkScheduleToShared 의 재변환을 상쇄
  if (primaryStart && primaryEnd) {
    data.start_time = `${toWallClock(primaryStart)}Z`;
    data.end_time = `${toWallClock(primaryEnd)}Z`;
  }

  return data;
};
